import { Injectable } from '@angular/core';
import {CrudService} from './services';
import {Invoice} from './dto/invoice';
import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {DataListModel} from './models/data-list-model';
import {Payment} from './dto/payment';
import {DebtorServiceLogEntity} from './dto/debtor-service-log-entity';
import {DateTableFilter} from './table-datasouces/date-table-filter';
import {PrevNextModel} from './models/prev-next-model';
import {toISODateString, toServerDateString} from './providers/app-date-adapter';
import {AnnualFeeStatsFilteringModel} from './models/annual-fee-stats-filtering-model';
import {AnnualFeeStatsModel} from './models/annual-fee-stats-model';
import {TrustPayTransaction} from './dto/trustPayTransaction';
import {CardRenewalImport} from './dto/card-renewal-import';
import {TrustPayTransactionImportResult} from './dto/trust-pay-transaction-import-result';
import {InvoiceSearch} from './dto/invoice-search';
import {CardSearch} from './dto/card-search';


@Injectable({
  providedIn: 'root'
})
export class TrustPayService implements CrudService<TrustPayTransaction> {

  readonly apiPath = 'trustpay/';

  constructor(protected http: HttpClient) { }
  get(id: any) {
    return this.http.get(this.apiPath + id) as Observable<TrustPayTransaction>;
  }
  getMany(
    pageIndex: number,
    pageSize: number,
    sortField: string,
    sortDirection: string,
    filterString: string,
    dateFilterCols: string[],
    startFilterDate: Date,
    endFilterDate: Date,
    someAdditionalData: string) {
    const params = new HttpParams()
      .set('page', pageIndex.toString())
      .set('pageSize', pageSize.toString())
      .set('filterString', filterString)
      .set('sortField', sortField)
      .set( 'sortDirection', sortDirection )
      .set( 'someAdditionalData', someAdditionalData );
    return this.http.get(this.apiPath, { params }) as Observable<DataListModel<TrustPayTransaction>>;
  }

  getByCardId( cardId: number ): Observable<TrustPayTransaction[]> {
    return this.http.get(this.apiPath+'by-card-id/'+cardId) as Observable<TrustPayTransaction[]>;
  }

  getIsBusy(): Observable<boolean> {
    return this.http.get(this.apiPath+'is-busy', {headers:{'skip-error-snack':'true'}}) as Observable<boolean>;
  }

  add(item: TrustPayTransaction): Observable<Object> {
    throw new Error('Method not implemented.');
  }
  update(item: Invoice) {
    return this.http.put(this.apiPath, item) as Observable<TrustPayTransaction>;
  }

  delete(id: any) {
    return this.deleteMany([id]);
  }
  restore(id: any) {
    return this.restoreMany([id]);
  }

  deleteMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();

    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });

    return this.http.delete(this.apiPath, { params: httpParams });
  }

  restoreMany(idList: any[]): Observable<Object> {
    let httpParams = new HttpParams();
    idList.forEach(id => {
      httpParams = httpParams.append('list', id.toString());
    });
    return this.http.patch(this.apiPath, null, { params: httpParams });
  }


  getPrev(filters: DateTableFilter, TrustPayTransactionId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${TrustPayTransactionId}/prev`, { params }) as Observable<number>;
  }

  getNext(filters: DateTableFilter, TrustPayTransactionId: number): Observable<number> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${TrustPayTransactionId}/next`, { params }) as Observable<number>;
  }

  getPrevNext(filters: DateTableFilter, TrustPayTransactionId: number): Observable<PrevNextModel> {
    const params = this.getFilterParams(filters);
    return this.http.get(`${this.apiPath}${TrustPayTransactionId}/prev-next`, { params }) as Observable<PrevNextModel>;
  }



  getFilterParams(filters: DateTableFilter): HttpParams {
    const str =  filters != null ? filters.str || '' : '';
    const dateFilterCols = filters != null ? filters.dateFilterCols || [] : [];
    const startFilterDate = filters != null ? filters.startFilterDate || null : null;
    const endFilterDate = filters != null ? filters.endFilterDate || null : null;
    const someAdditionalData = filters != null ? filters.someAdditionalData || null : null;

    let params = new HttpParams()


      .set('filterString', str)
      .set('startFilterDate', toISODateString(startFilterDate))
      .set('endFilterDate', toISODateString(endFilterDate))
      .set('someAdditionalData', someAdditionalData);

    if (filters != null && filters.sortField) {
      params = params.set('sortField', filters.sortField);
    }
    if (filters != null && filters.sortDirection) {
      params = params.set('sortDirection', filters.sortDirection);
    }

    if (filters != null && filters.enumFilter) {
      params = params.set('enumFilter', filters.enumFilter.value+'_'+filters.enumFilter.key);
    }


    if (dateFilterCols instanceof Array) {
      dateFilterCols.forEach(element => {
        params = params.append('dateFilterCols', element);
      });
    }

    return params;
  }

  uploadImportCsv(file: File): Observable<TrustPayTransaction[]> { // TODO implement / change
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.apiPath + 'upload', file) as Observable<TrustPayTransaction[]>;
  }

  uploadImportCsvCommerzbank(file: File): Observable<TrustPayTransaction[]> { // TODO implement / change
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.apiPath + 'upload-commerzbank', file) as Observable<TrustPayTransaction[]>;
  }

  searchCards(term: string) {

    let params = new HttpParams();

    if( term != null ) {
      params = params.set('term', term);
    }

    return this.http.get(`${this.apiPath}search-cards`, { params }) as Observable<CardSearch[]>;
  }

  linkTransactionToCard(transactionId: number, cardId: number) {
    return this.http.patch(this.apiPath + 'link-trans-card/' + transactionId + '/' + cardId, null);
  }

  unlinkTransactionFromCard(transactionId: number, cardId: number){
    return this.http.delete(this.apiPath + 'unlink-trans-card/' + transactionId + '/' + cardId);
  }

  refundTransaction(transactionId: number) {
    return this.http.patch(this.apiPath + 'refund-trans/' + transactionId, null);
  }
  undoRefundTransaction(transactionId: number){
    return this.http.delete(this.apiPath + 'undo-refund-trans/' + transactionId);
  }

  getNeedsCheck() : Observable<TrustPayTransaction[]> {
    return this.http.get(`${this.apiPath}needs-check`) as Observable<TrustPayTransaction[]>;
  }

  confirmUserCheck(id: number) {
    return this.http.patch(this.apiPath + 'confirm-user-check', [id]);
  }

  confirmUserCheckMany(ids: number[]) {
    return this.http.patch(this.apiPath + 'confirm-user-check', ids);
  }
}
